import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  ArrowLeft,
  Building2,
  Globe2,
  MapPin,
  Scale,
  Search,
  ShieldCheck,
} from "lucide-react";

import { apiFetch } from "../../context/AppContext";
import { DEFAULT_COMPLIANCE } from "../../constants/complianceRegions";
import { scoreBg, scoreColor } from "../../components/common/gapAssessmentTheme";

const COUNTRY_LAW_MATCHES = {
  Indonesia: {
    law: "PDP Law",
    official: "UU No. 27 Tahun 2022",
    modules: [
      {
        key: "gap",
        label: "Gap Assessment",
        path: "/user/gap-assessment",
        match: "Governance, lawful basis, data-subject rights, security, and incidents",
        articles: "Arts. 5, 16, 21, 31, 36, 51",
        workstreams: ["GOV", "BAS", "DSR", "SEC", "INC", "ENR"],
      },
      {
        key: "pia",
        label: "PIA",
        path: "/user/pia",
        match: "Impact assessments and automated / high-risk processing",
        articles: "Arts. 34, 44",
        workstreams: ["RISK", "AUTO", "CH"],
      },
      {
        key: "ropa",
        label: "ROPA",
        path: "/user/ropa",
        match: "Records of processing, data lifecycle, and purpose mapping",
        articles: "Arts. 21, 28, 31",
        workstreams: ["DATA", "GOV", "BAS"],
      },
      {
        key: "tprm",
        label: "TPRM",
        path: "/user/tprm",
        match: "Processors, vendors, and cross-border transfers",
        articles: "Arts. 36, 50, 51",
        workstreams: ["PROC", "XB", "INC"],
      },
    ],
  },
  India: {
    law: "DPDP Act",
    official: "Digital Personal Data Protection Act, 2023",
    modules: [
      {
        key: "gap",
        label: "Gap Assessment",
        path: "/user/gap-assessment",
        match: "Consent, notice, principal rights, fiduciary duties, and governance",
        articles: "§5–8, §11–14",
        workstreams: ["GOV", "BAS", "DSR", "SEC"],
      },
      {
        key: "pia",
        label: "PIA",
        path: "/user/pia",
        match: "High-risk processing and significant data fiduciary assessments",
        articles: "§8, §10",
        workstreams: ["RISK", "AUTO"],
      },
      {
        key: "ropa",
        label: "ROPA",
        path: "/user/ropa",
        match: "Processing records, purpose, retention, and sharing",
        articles: "§8, §16",
        workstreams: ["DATA", "GOV"],
      },
      {
        key: "tprm",
        label: "TPRM",
        path: "/user/tprm",
        match: "Processor contracts and cross-border transfers",
        articles: "§8(2), §16",
        workstreams: ["PROC", "XB"],
      },
    ],
  },
};

const GENERIC_MODULES = [
  { key: "gap", label: "Gap Assessment", path: "/user/gap-assessment", match: "Organisation-wide privacy control assessment", articles: "", workstreams: [] },
  { key: "pia", label: "PIA", path: "/user/pia", match: "Privacy impact assessments for high-risk processing", articles: "", workstreams: [] },
  { key: "ropa", label: "ROPA", path: "/user/ropa", match: "Records of processing activities", articles: "", workstreams: [] },
  { key: "tprm", label: "TPRM", path: "/user/tprm", match: "Vendor and processor compliance", articles: "", workstreams: [] },
];

const COUNTRY_LAWS = {
  Indonesia: "PDP Law",
  India: "DPDP Act",
  Malaysia: "PDPA",
  Singapore: "PDPA",
  "South Africa": "POPIA",
  Nigeria: "NDPA",
  "European Union": "GDPR",
  "United States": "CCPA",
};

const ANSWER_STYLE = {
  Yes: "border-emerald-200 bg-emerald-50 text-emerald-700",
  Partially: "border-amber-200 bg-amber-50 text-amber-700",
  No: "border-rose-200 bg-rose-50 text-rose-700",
  "N/A": "border-slate-200 bg-slate-100 text-slate-600",
};

function answersForQuestion(byQuestion, questionId, departmentId) {
  const rows = byQuestion?.[questionId] || [];
  if (!departmentId) return rows;
  return rows.filter((row) => row.department_id === departmentId);
}

function DepartmentAnswers({ responses, departmentSelected }) {
  if (!responses.length) {
    if (!departmentSelected) return null;
    return (
      <p className="mt-2 text-xs text-slate-400">Not answered by this department</p>
    );
  }
  return (
    <ul className="mt-2 flex flex-wrap gap-1.5">
      {responses.map((row) => (
        <li
          key={row.department_id}
          className={`inline-flex max-w-full items-center gap-1.5 rounded-md border px-1.5 py-0.5 text-[11px] ${
            ANSWER_STYLE[row.answer] || "border-slate-200 bg-slate-50 text-slate-600"
          }`}
        >
          <span className="truncate font-semibold">{row.department_name}</span>
          <span className="font-medium">{row.answer}</span>
          {row.status === "in_progress" && (
            <span className="font-medium opacity-70">In progress</span>
          )}
        </li>
      ))}
    </ul>
  );
}

function lawMatchFor(country) {
  if (COUNTRY_LAW_MATCHES[country]) return COUNTRY_LAW_MATCHES[country];
  return {
    law: COUNTRY_LAWS[country] || "Local Regulation",
    official: country ? `${country} privacy law` : "",
    modules: GENERIC_MODULES,
  };
}

export default function ComplianceChecklist() {
  const navigate = useNavigate();
  const location = useLocation();

  const [complianceOptions, setComplianceOptions] = useState(DEFAULT_COMPLIANCE);
  const [selectedRegion, setSelectedRegion] = useState(location.state?.region || "");
  const [selectedCountry, setSelectedCountry] = useState(location.state?.country || "");
  const [checklist, setChecklist] = useState([]);
  const [regulation, setRegulation] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [lawMapping, setLawMapping] = useState(null);
  const [dpdpSections, setDpdpSections] = useState([]);
  const [sectionFilter, setSectionFilter] = useState("All");
  const [query, setQuery] = useState("");
  const [departmentFilter, setDepartmentFilter] = useState("");
  const [departmentAnswers, setDepartmentAnswers] = useState({ departments: [], by_question: {} });
  const [countryScores, setCountryScores] = useState({});
  const [countryDepartmentScores, setCountryDepartmentScores] = useState({});

  useEffect(() => {
    let cancelled = false;
    const loadComplianceSettings = async () => {
      try {
        const [res, overviewRes] = await Promise.all([
          apiFetch("/tenant/settings/global-compliance"),
          apiFetch("/tenant/settings/global-compliance/overview"),
        ]);
        const data = await res.json();
        const overview = await overviewRes.json().catch(() => ({}));
        if (cancelled) return;
        if (res.ok && Array.isArray(data?.selections) && data.selections.length) {
          setComplianceOptions(data.selections);
        }
        if (overviewRes.ok) {
          const scores = {};
          const byDepartment = {};
          (overview.countries || []).forEach((item) => {
            if (!item.country) return;
            scores[item.country] = item.score ?? null;
            const rows = {};
            (item.department_scores || []).forEach((row) => {
              if (row.department_id) rows[row.department_id] = row.score ?? null;
            });
            byDepartment[item.country] = rows;
          });
          setCountryScores(scores);
          setCountryDepartmentScores(byDepartment);
        }
      } catch (loadError) {
        console.error("Unable to load compliance settings:", loadError);
      }
    };
    loadComplianceSettings();
    return () => {
      cancelled = true;
    };
  }, []);

  const regions = useMemo(
    () => [...new Set(complianceOptions.map((item) => item.region).filter(Boolean))].sort(),
    [complianceOptions],
  );

  const countries = useMemo(() => {
    if (!selectedRegion) return [];
    return complianceOptions
      .filter((item) => item.region === selectedRegion)
      .map((item) => item.country)
      .filter(Boolean)
      .sort();
  }, [selectedRegion, complianceOptions]);

  useEffect(() => {
    if (!selectedRegion || !selectedCountry) {
      setChecklist([]);
      setRegulation("");
      setError("");
      setLawMapping(null);
      setDpdpSections([]);
      return undefined;
    }

    let cancelled = false;
    const loadChecklist = async () => {
      setLoading(true);
      setError("");
      try {
        const isIndia = selectedCountry.trim().toLowerCase() === "india";
        const params = `region=${encodeURIComponent(selectedRegion)}&country=${encodeURIComponent(selectedCountry)}`;
        const [res, mapRes, bankRes, overviewRes] = await Promise.all([
          apiFetch(`/tenant/compliance-checklists/items?${params}`),
          apiFetch(`/tenant/compliance-checklists/law-mapping?${params}`),
          isIndia ? apiFetch("/gap-assessment/sections") : Promise.resolve(null),
          apiFetch("/tenant/settings/global-compliance/overview"),
        ]);
        const data = await res.json().catch(() => ({}));
        const mapData = await mapRes.json().catch(() => ({}));
        const bankData = bankRes ? await bankRes.json().catch(() => ({})) : {};
        const overview = await overviewRes.json().catch(() => ({}));
        if (cancelled) return;
        if (overviewRes.ok) {
          const scores = {};
          const byDepartment = {};
          (overview.countries || []).forEach((item) => {
            if (!item.country) return;
            scores[item.country] = item.score ?? null;
            const rows = {};
            (item.department_scores || []).forEach((row) => {
              if (row.department_id) rows[row.department_id] = row.score ?? null;
            });
            byDepartment[item.country] = rows;
          });
          setCountryScores(scores);
          setCountryDepartmentScores(byDepartment);
        }
        if (!res.ok && !isIndia) {
          throw new Error(data.detail || "Unable to load checklist");
        }
        setChecklist(res.ok ? (data.items || []) : []);
        setRegulation(res.ok ? (data.regulation || "") : "");
        setLawMapping(mapRes.ok ? mapData : null);
        setDpdpSections(isIndia && bankRes?.ok ? (bankData.sections || []) : []);
        if (isIndia && bankRes && !bankRes.ok && !res.ok) {
          throw new Error(bankData.detail || data.detail || "Unable to load DPDP questions");
        }
      } catch (loadError) {
        if (!cancelled) {
          setChecklist([]);
          setError(loadError.message);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    loadChecklist();
    return () => {
      cancelled = true;
    };
  }, [selectedRegion, selectedCountry]);

  useEffect(() => {
    if (!selectedRegion || !selectedCountry) {
      setDepartmentAnswers({ departments: [], by_question: {} });
      return undefined;
    }
    let cancelled = false;
    const loadDepartmentAnswers = async () => {
      try {
        const res = await apiFetch("/gap-assessment/department-answers");
        const data = await res.json().catch(() => ({}));
        if (cancelled || !res.ok) return;
        setDepartmentAnswers({
          departments: data.departments || [],
          by_question: data.by_question || {},
        });
      } catch (loadError) {
        console.error("Unable to load department responses:", loadError);
      }
    };
    loadDepartmentAnswers();
    return () => {
      cancelled = true;
    };
  }, [selectedRegion, selectedCountry]);

  const checklistVisible = Boolean(selectedRegion) && Boolean(selectedCountry);
  const isMasterLaw = selectedCountry.trim().toLowerCase() === "india";
  const lawMatch = lawMatchFor(selectedCountry);
  const mappedRows = useMemo(() => {
    const itemsById = new Map((checklist || []).map((item) => [item.id, item]));
    const rows = [];
    (lawMapping?.links || []).forEach((link) => {
      const questions = (link.questions || []).filter((question) => question.module === "gap");
      if (!questions.length) return;
      rows.push({
        id: link.checklist_item_id,
        section: link.section || questions[0]?.section || "General",
        item: itemsById.get(link.checklist_item_id) || { title: link.checklist_item_id },
        questions,
      });
    });
    rows.sort((left, right) => left.section.localeCompare(right.section));
    return rows;
  }, [lawMapping, checklist]);
  const masterRows = useMemo(() => (
    dpdpSections.flatMap((section) => (section.questions || []).map((question) => ({
      id: question.id,
      section: section.title || section.id || "DPDP",
      master: true,
      item: {
        title: question.text || question.title || "",
        regulation: question.section || question.dpdp_ref || "",
      },
      questions: [],
    })))
  ), [dpdpSections]);
  const rowsForView = isMasterLaw ? masterRows : mappedRows;
  const mappingActive = isMasterLaw ? masterRows.length > 0 : Boolean(lawMapping?.mapped) && mappedRows.length > 0;
  const displayLawName = isMasterLaw ? "DPDP Act" : (lawMapping?.law_name || lawMatch.law);
  const displayOfficial = isMasterLaw
    ? "Digital Personal Data Protection Act, 2023"
    : (lawMapping?.official_name || lawMatch.official);
  const sectionNames = [...new Set(rowsForView.map((row) => row.section))];
  const questionCount = rowsForView.length;
  const complianceScore = !selectedCountry
    ? null
    : departmentFilter
      ? (countryDepartmentScores[selectedCountry]?.[departmentFilter] ?? null)
      : (countryScores[selectedCountry] ?? null);
  const scoreText = complianceScore == null ? "—" : `${complianceScore}%`;
  const sectionCounts = useMemo(() => {
    const counts = new Map();
    rowsForView.forEach((row) => counts.set(row.section, (counts.get(row.section) || 0) + 1));
    return counts;
  }, [rowsForView]);
  const visibleRows = useMemo(() => {
    const needle = query.trim().toLowerCase();
    return rowsForView.filter((row) => {
      if (sectionFilter !== "All" && row.section !== sectionFilter) return false;
      if (!needle) return true;
      const haystack = [
        row.item.title,
        row.item.regulation,
        row.section,
        ...(row.questions || []).map((question) => `${question.ref || ""} ${question.title || ""}`),
      ].join(" ").toLowerCase();
      return haystack.includes(needle);
    });
  }, [rowsForView, sectionFilter, query]);

  const sectionOptions = ["All", ...sectionNames];

  return (
    <div className="relative -m-4 flex h-[calc(100dvh-6.5rem)] flex-col overflow-hidden bg-slate-100 lg:-m-6">
      <header className="shrink-0 border-b border-slate-200 bg-white px-4 py-3">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex min-w-0 items-center gap-3">
            <button
              type="button"
              onClick={() => navigate("/user/global-compliance")}
              title="Back to Global Compliance"
              className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-lg text-slate-500 transition hover:bg-slate-100 hover:text-slate-800"
            >
              <ArrowLeft className="h-4 w-4" />
            </button>
            <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-blue-600 text-white">
              <Scale className="h-4 w-4" />
            </div>
            <div className="min-w-0">
              <h1 className="text-base font-bold tracking-tight text-slate-900">Compliance by Law</h1>
              <p className="truncate text-xs text-slate-500">
                {checklistVisible
                  ? `${selectedCountry} · ${displayLawName || "Law"}${displayOfficial ? ` · ${displayOfficial}` : ""}`
                  : "Pick a region and country to read that law’s questions."}
              </p>
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <label className="relative">
              <Globe2 className="pointer-events-none absolute left-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-slate-400" />
              <select
                aria-label="Region"
                value={selectedRegion}
                onChange={(event) => {
                  setSelectedRegion(event.target.value);
                  setSelectedCountry("");
                  setSectionFilter("All");
                  setQuery("");
                  setDepartmentFilter("");
                }}
                className="h-9 appearance-none rounded-lg border border-slate-200 bg-white pl-8 pr-8 text-sm font-medium text-slate-800 outline-none focus:border-blue-500"
              >
                <option value="">Region</option>
                {regions.map((region) => (
                  <option key={region} value={region}>{region}</option>
                ))}
              </select>
            </label>
            <label className="relative">
              <MapPin className="pointer-events-none absolute left-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-slate-400" />
              <select
                aria-label="Country"
                value={selectedCountry}
                onChange={(event) => {
                  setSelectedCountry(event.target.value);
                  setSectionFilter("All");
                  setQuery("");
                  setDepartmentFilter("");
                }}
                disabled={!selectedRegion}
                className="h-9 appearance-none rounded-lg border border-slate-200 bg-white pl-8 pr-8 text-sm font-medium text-slate-800 outline-none focus:border-blue-500 disabled:bg-slate-50 disabled:text-slate-400"
              >
                <option value="">{selectedRegion ? "Country" : "Select region first"}</option>
                {countries.map((country) => (
                  <option key={country} value={country}>{country}</option>
                ))}
              </select>
            </label>
          </div>
        </div>
        {checklistVisible && (
          <div className="mt-3 flex flex-wrap gap-2 border-t border-slate-100 pt-3">
            {[
              ["Country", selectedCountry],
              ["Law", displayLawName || "Not set"],
              ["Score", scoreText],
              ["Sections", mappingActive ? sectionNames.length : 0],
              ["Questions", mappingActive ? questionCount : 0],
            ].map(([label, value]) => {
              const scored = label === "Score" && complianceScore != null;
              return (
                <span
                  key={label}
                  title={`${label}: ${value}`}
                  className="inline-flex items-center gap-1.5 rounded-full border border-slate-200 bg-slate-50 px-2.5 py-1 text-xs"
                  style={scored ? { color: scoreColor(complianceScore), background: scoreBg(complianceScore), borderColor: `${scoreColor(complianceScore)}55` } : undefined}
                >
                  <span className={`font-semibold uppercase tracking-wide ${scored ? "opacity-70" : "text-slate-400"}`}>{label}</span>
                  <span className={`font-bold ${scored ? "" : "text-slate-800"}`}>{value}</span>
                </span>
              );
            })}
            {regulation ? (
              <span title={regulation} className="inline-flex max-w-full items-center rounded-full border border-slate-200 bg-white px-2.5 py-1 text-xs font-medium text-slate-500">
                <span className="truncate">{regulation}</span>
              </span>
            ) : null}
          </div>
        )}
      </header>

      {!checklistVisible ? (
        <div className="flex flex-1 flex-col items-center justify-center px-6 text-center">
          <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-white text-blue-600 shadow-sm">
            <ShieldCheck className="h-7 w-7" />
          </div>
          <h2 className="mt-4 text-base font-bold text-slate-900">Select a region and country</h2>
          <p className="mt-2 max-w-md text-sm leading-6 text-slate-500">
            The questions for that privacy law open in the panel below.
          </p>
        </div>
      ) : loading ? (
        <p className="flex flex-1 items-center justify-center text-sm text-slate-500">Loading questions…</p>
      ) : error ? (
        <p className="flex flex-1 items-center justify-center px-6 text-center text-sm text-red-600">{error}</p>
      ) : !mappingActive ? (
        <div className="flex flex-1 flex-col items-center justify-center px-6 text-center">
          <p className="text-sm font-semibold text-slate-800">No questions yet</p>
          <p className="mt-2 max-w-md text-sm text-slate-500">
            {isMasterLaw
              ? "DPDP questions for India could not be loaded."
              : "Your tenant admin maps this country’s questions to DPDP. They appear here after that mapping is saved."}
          </p>
        </div>
      ) : (
        <div className="flex min-h-0 flex-1">
          <nav className="hidden w-64 shrink-0 flex-col overflow-y-auto border-r border-slate-200 bg-white lg:flex" aria-label="Sections">
            <p className="px-4 pb-1 pt-3 text-[11px] font-bold uppercase tracking-wide text-slate-400">Sections</p>
            {sectionOptions.map((section) => {
              const active = sectionFilter === section;
              const count = section === "All" ? questionCount : (sectionCounts.get(section) || 0);
              return (
                <button
                  key={section}
                  type="button"
                  onClick={() => setSectionFilter(section)}
                  className={`flex items-center justify-between gap-3 px-4 py-2 text-left text-sm transition ${
                    active ? "bg-blue-50 font-semibold text-blue-700" : "text-slate-700 hover:bg-slate-50"
                  }`}
                >
                  <span className="truncate">{section === "All" ? "All sections" : section}</span>
                  <span className={`shrink-0 text-xs tabular-nums ${active ? "text-blue-500" : "text-slate-400"}`}>{count}</span>
                </button>
              );
            })}
          </nav>

          <div className="flex min-w-0 flex-1 flex-col">
            <div className="flex shrink-0 items-center gap-2 border-b border-slate-200 bg-white px-3 py-2">
              <label className="min-w-0 lg:hidden">
                <span className="sr-only">Section</span>
                <select
                  aria-label="Section"
                  value={sectionFilter}
                  onChange={(event) => setSectionFilter(event.target.value)}
                  className="h-9 max-w-[11rem] rounded-lg border border-slate-200 bg-white px-2 text-sm font-medium text-slate-800 outline-none focus:border-blue-500"
                >
                  {sectionOptions.map((section) => (
                    <option key={section} value={section}>
                      {section === "All" ? "All sections" : section}
                    </option>
                  ))}
                </select>
              </label>
              <label className="relative min-w-0 flex-1">
                <Search className="pointer-events-none absolute left-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-slate-400" />
                <input
                  type="search"
                  value={query}
                  onChange={(event) => setQuery(event.target.value)}
                  placeholder="Search questions"
                  className="h-9 w-full rounded-lg border border-slate-200 bg-slate-50 pl-8 pr-3 text-sm text-slate-800 outline-none placeholder:text-slate-400 focus:border-blue-500 focus:bg-white"
                />
              </label>
              {departmentAnswers.departments.length > 0 && (
                <label className="relative shrink-0">
                  <Building2 className="pointer-events-none absolute left-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-slate-400" />
                  <select
                    aria-label="Department"
                    value={departmentFilter}
                    onChange={(event) => setDepartmentFilter(event.target.value)}
                    className="h-9 max-w-[14rem] appearance-none rounded-lg border border-slate-200 bg-white pl-8 pr-8 text-sm font-medium text-slate-800 outline-none focus:border-blue-500"
                  >
                    <option value="">All departments</option>
                    {departmentAnswers.departments.map((department) => (
                      <option key={department.id} value={department.id}>
                        {department.name}
                      </option>
                    ))}
                  </select>
                </label>
              )}
              <span className="shrink-0 text-xs font-medium text-slate-400">{visibleRows.length}</span>
            </div>

            <div className="min-h-0 flex-1 overflow-y-auto px-3 py-3 sm:px-4">
              {visibleRows.length === 0 ? (
                <p className="py-16 text-center text-sm text-slate-500">No questions match that search.</p>
              ) : (
                <div className="mx-auto flex max-w-4xl flex-col gap-2">
                  {visibleRows.map((row, index) => {
                    const previous = visibleRows[index - 1];
                    const showHeading = sectionFilter === "All" && row.section !== previous?.section;
                    const details = [
                      row.item.evidence && ["Evidence", row.item.evidence],
                      row.item.owner && ["Owner", row.item.owner],
                      row.item.priority && ["Priority", row.item.priority],
                      row.item.status && ["Status", row.item.status],
                      row.item.notes && ["Notes", row.item.notes],
                    ].filter(Boolean);
                    return (
                      <div key={`${row.id}-${row.section}`}>
                        {showHeading && (
                          <h2 className={`mb-2 px-1 text-xs font-bold uppercase tracking-wide text-slate-500 ${index === 0 ? "pt-1" : "mt-4"}`}>
                            {row.section}
                            <span className="ml-2 font-semibold tabular-nums text-slate-400">{sectionCounts.get(row.section) || 0}</span>
                          </h2>
                        )}
                        <article className="rounded-xl border border-slate-200 bg-white px-4 py-3 shadow-sm">
                          {(row.item.regulation || (row.item.category && row.item.category.toLowerCase() !== "general")) && (
                          <div className="mb-1.5 flex flex-wrap items-center gap-2">
                            {row.item.regulation && (
                              <span className="rounded-md bg-blue-50 px-1.5 py-0.5 text-[11px] font-semibold text-blue-700">{row.item.regulation}</span>
                            )}
                            {row.item.category && row.item.category.toLowerCase() !== "general" && (
                              <span className="rounded-md bg-slate-100 px-1.5 py-0.5 text-[11px] font-semibold text-slate-600">{row.item.category}</span>
                            )}
                          </div>
                          )}
                          <h3 className="text-sm font-medium leading-6 text-slate-900">{row.item.title}</h3>
                          {row.master && departmentAnswers.departments.length > 0 && (
                            <DepartmentAnswers
                              responses={answersForQuestion(
                                departmentAnswers.by_question,
                                row.id,
                                departmentFilter,
                              )}
                              departmentSelected={Boolean(departmentFilter)}
                            />
                          )}
                          {details.length > 0 && (
                            <dl className="mt-2 grid gap-1 sm:grid-cols-2">
                              {details.map(([label, value]) => (
                                <div key={label} className="text-xs leading-5 text-slate-500">
                                  <span className="font-semibold text-slate-700">{label}: </span>
                                  {value}
                                </div>
                              ))}
                            </dl>
                          )}
                          {row.questions.length > 0 && (
                            <div className="mt-3 rounded-lg border border-slate-100 bg-slate-50 px-3 py-2">
                              <p className="text-[10px] font-bold uppercase tracking-wide text-slate-500">Mapped DPDP</p>
                              <ul className="mt-1.5 space-y-1.5">
                                {row.questions.map((question) => (
                                  <li key={question.id} className="text-sm leading-5 text-slate-800">
                                    {question.ref && <span className="mr-2 font-semibold text-blue-700">{question.ref}</span>}
                                    {question.title}
                                    {departmentAnswers.departments.length > 0 && (
                                      <DepartmentAnswers
                                        responses={answersForQuestion(
                                          departmentAnswers.by_question,
                                          question.id,
                                          departmentFilter,
                                        )}
                                        departmentSelected={Boolean(departmentFilter)}
                                      />
                                    )}
                                  </li>
                                ))}
                              </ul>
                            </div>
                          )}
                        </article>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
